import React, { useState, useEffect } from 'react';
import { Download, Bell, BellRing, Check, Smartphone } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstallAppSection = ({ content }) => {
  const { t, language } = useLanguage();  // Get current language
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [vapidPublicKey, setVapidPublicKey] = useState(null);

  // Fetch VAPID public key and listen for beforeinstallprompt
  useEffect(() => {
    // Fetch VAPID key from backend
    const fetchVapidKey = async () => {
      try {
        const response = await fetch(`${API}/vapid-public-key`);
        const data = await response.json();
        setVapidPublicKey(data.publicKey);
      } catch (error) {
        console.error('Failed to fetch VAPID key:', error);
      }
    };
    fetchVapidKey();

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
      }
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (isInstalled) {
      toast.success(t('install.alreadyInstalled'));
      return;
    }

    if (!deferredPrompt) {
      // Show manual instructions for iOS/Safari
      toast.info(t('install.manualInstructions'), {
        duration: 8000,
        description: t('install.iosInstructions')
      });
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      toast.success(t('install.success'));
    }
    setDeferredPrompt(null);
  };

  const handleEnableNotifications = async () => {
    if (!('Notification' in window)) {
      toast.error(t('notifications.notSupported'));
      return;
    }

    if (Notification.permission === 'denied') {
      toast.error(t('notifications.blocked'), {
        description: t('notifications.enableInSettings')
      });
      return;
    }

    if (!vapidPublicKey) {
      toast.error('Unable to enable notifications. Please try again.');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        
        // Register for push notifications
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          const registration = await navigator.serviceWorker.ready;
          
          // Subscribe to push notifications
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
          });

          // Save subscription to backend
          await saveSubscription(subscription);
          
          toast.success(t('notifications.enabled'), {
            description: t('notifications.willReceive')
          });
          
          // Show a test notification
          showTestNotification();
        }
      } else {
        toast.info(t('notifications.permissionDenied'));
      }
    } catch (error) {
      console.error('Notification error:', error);
      toast.error(t('notifications.error'));
    }
  };

  const saveSubscription = async (subscription) => {
    try {
      await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: language  // Send user's current language preference
        })
      });
    } catch (error) {
      console.error('Failed to save subscription:', error);
    }
  };

  const showTestNotification = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification('Road to Jannah', {
          body: t('notifications.testMessage'),
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-72x72.png',
          tag: 'test-notification',
          vibrate: [200, 100, 200]
        });
      });
    }
  };

  // Helper function to convert VAPID key
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  return (
    <section 
      className="py-16 md:py-20 bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10"
      data-testid="install-app-section"
    >
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground font-semibold mb-4">
            {t('install.title')}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('install.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Install App Card */}
          <Card className="border-primary/20 hover:border-primary/40 transition-colors overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg">
                  <Smartphone className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    {t('install.downloadApp')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('install.accessAnytime')}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  {t('install.feature1')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  {t('install.feature2')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  {t('install.feature3')}
                </li>
              </ul>

              <Button 
                onClick={handleInstallClick}
                className={`w-full gap-2 text-base py-6 ${
                  isInstalled 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-primary hover:bg-primary/90'
                }`}
                data-testid="install-app-btn"
              >
                {isInstalled ? (
                  <>
                    <Check className="h-5 w-5" />
                    {t('install.installed')}
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5" />
                    {t('install.installNow')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Notifications Card */}
          <Card className="border-accent/20 hover:border-accent/40 transition-colors overflow-hidden">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center shadow-lg">
                  <BellRing className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-foreground">
                    {t('notifications.title')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('notifications.subtitle')}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  {t('notifications.feature1')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  {t('notifications.feature2')}
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  {t('notifications.feature3')}
                </li>
              </ul>

              <Button 
                onClick={handleEnableNotifications}
                disabled={notificationPermission === 'denied'}
                className={`w-full gap-2 text-base py-6 ${
                  notificationsEnabled 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-accent hover:bg-accent/90 text-accent-foreground'
                }`}
                data-testid="enable-notifications-btn"
              >
                {notificationsEnabled ? (
                  <>
                    <Check className="h-5 w-5" />
                    {t('notifications.enabled')}
                  </>
                ) : notificationPermission === 'denied' ? (
                  <>
                    <Bell className="h-5 w-5" />
                    {t('notifications.blocked')}
                  </>
                ) : (
                  <>
                    <Bell className="h-5 w-5" />
                    {t('notifications.enable')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default InstallAppSection;
